import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MakeupSchedule } from '@/types';
import { formatDate } from '@/lib/datetime';

export interface OverdueMakeup extends MakeupSchedule {
  studentName: string;
}

interface OverdueMakeupAlertProps {
  makeups: OverdueMakeup[];
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS[d.getDay()];
}

function daysOverdue(dateStr: string): number {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

export default function OverdueMakeupAlert({ makeups }: OverdueMakeupAlertProps) {
  const [expanded, setExpanded] = useState(true);

  if (makeups.length === 0) return null;

  return (
    <section className="rounded-2xl border border-accent-300 bg-accent-100/60 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-alarm-warning-line text-lg"></i>
          </span>
          <div>
            <h2 className="text-base font-bold text-accent-900">
              보충 예정일이 지난 일정이 {makeups.length}건 있어요
            </h2>
            <p className="mt-0.5 text-sm text-accent-800/90">
              수업일지를 아직 작성하지 않은 보충 일정이에요. 확인하고 수업일지를 남겨주세요.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 rounded-md border border-accent-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-accent-800 transition hover:bg-background-100 whitespace-nowrap"
        >
          <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
          {expanded ? '접기' : '펼치기'}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {makeups.map((m) => {
            const overdue = daysOverdue(m.scheduled_date);
            return (
              <Link
                key={m.id}
                to={`/students/${m.student_id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-accent-200 bg-background-50 px-4 py-3 transition hover:border-accent-400"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className="ri-calendar-schedule-line"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">
                      {m.studentName}
                      <span className="ml-1.5 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
                        {m.reason || '보충'}
                      </span>
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      예정일 {formatDate(m.scheduled_date)}
                      {weekdayLabel(m.scheduled_date) && ` (${weekdayLabel(m.scheduled_date)})`}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] font-bold text-background-50 whitespace-nowrap">
                  {overdue}일 지남
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}